import { useEffect, useRef, useState } from 'react';
import { MicIcon, MicOffIcon } from '../components/icons';
import { cx } from '../components/ui';
import { clock } from '../lib/format';
import { usePrefersReducedMotion } from '../lib/useReducedMotion';
import { waveformEnvelope } from '../lib/waveform';
import { InputLevel, SILENCE_THRESHOLD } from './level';
import { Countdown } from './RecorderStage';
import type { RecorderApi } from './useRecorder';

/**
 * What the person recording a voice note sees.
 *
 * A screen recorder can show you its own output; an audio recorder has nothing
 * to show. So this stage is a meter, not a preview: a waveform-shaped bar
 * array that lights up left to right with the microphone's level, and says so
 * in words when it has been hearing nothing for a while. If the wrong input is
 * selected or the mic is muted in hardware, that is visible in the first
 * second rather than at the end of a five-minute take.
 */

const BAR_COUNT = 48;

/**
 * A fixed envelope, so the meter reads as a waveform rather than a graphic
 * equaliser. It is deliberately not derived from the audio: a real scrolling
 * waveform is motion nobody asked for, and the *level* is the information.
 */
const ENVELOPE: readonly number[] = waveformEnvelope('slate-recorder', BAR_COUNT);

/** Quantise before setting state: 60 renders a second to move one pixel is waste. */
function quantise(value: number, steps: number): number {
  return Math.round(value * steps) / steps;
}

/**
 * Live microphone level, 0 to 1.
 *
 * With motion allowed this samples every frame and applies an asymmetric
 * smoother — fast attack so a syllable registers immediately, slow release so
 * the meter does not strobe between words. With reduced motion it samples five
 * times a second and does not smooth at all: the level is still shown, it just
 * steps rather than glides.
 */
function useInputLevel(
  stream: MediaStream | null,
  reduced: boolean,
): { level: number; measuring: boolean } {
  const [level, setLevel] = useState(0);
  const [measuring, setMeasuring] = useState(false);

  useEffect(() => {
    setLevel(0);
    setMeasuring(false);
    if (!stream) return;

    let meter: InputLevel;
    try {
      meter = new InputLevel(stream);
    } catch {
      // No Web Audio in this browser. The recording is unaffected; the meter
      // simply stays at rest rather than reporting a level it cannot measure.
      return;
    }

    // Safari in particular can hand back a suspended context here, since the
    // click that granted the microphone is several awaits ago by now.
    const wake = () => meter.ensureRunning();
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);

    const sample = (smoothing: number, steps: number, previous: number): number => {
      const raw = meter.read();
      // Fast attack, slow release: a syllable registers at once, and the meter
      // does not strobe in the gaps between words.
      const next = raw > previous ? raw : previous + (raw - previous) * smoothing;
      setLevel(quantise(next, steps));
      setMeasuring(meter.running);
      return next;
    };

    let smoothed = 0;
    let handle = 0;
    let timer = 0;

    if (reduced) {
      timer = window.setInterval(() => {
        smoothed = sample(1, 8, smoothed);
      }, 200);
    } else {
      const step = () => {
        smoothed = sample(0.12, 64, smoothed);
        handle = requestAnimationFrame(step);
      };
      handle = requestAnimationFrame(step);
    }

    return () => {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
      if (timer) window.clearInterval(timer);
      if (handle) cancelAnimationFrame(handle);
      meter.close();
    };
  }, [reduced, stream]);

  return { level, measuring };
}

/** True once nothing has been heard for a couple of seconds while listening. */
function useSilenceWarning(level: number, listening: boolean): boolean {
  const levelRef = useRef(level);
  levelRef.current = level;
  const [quiet, setQuiet] = useState(false);

  useEffect(() => {
    if (!listening) {
      setQuiet(false);
      return;
    }
    let quietSince: number | null = null;
    const timer = window.setInterval(() => {
      if (levelRef.current > SILENCE_THRESHOLD) {
        quietSince = null;
        setQuiet(false);
        return;
      }
      if (quietSince === null) quietSince = performance.now();
      else if (performance.now() - quietSince > 2500) setQuiet(true);
    }, 500);
    return () => window.clearInterval(timer);
  }, [listening]);

  return quiet;
}

function Meter({ level, reduced }: { level: number; reduced: boolean }) {
  // How many bars are lit reads as a level even when nothing is moving, which
  // is what carries the meter under reduced motion.
  const lit = Math.round(level * BAR_COUNT);

  return (
    <div
      className="flex h-24 w-full max-w-lg items-center justify-center gap-[3px] px-2"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(level * 100)}
      aria-label="Microphone input level"
    >
      {ENVELOPE.map((height, index) => (
        <span
          key={index}
          aria-hidden="true"
          className={cx(
            'w-full max-w-[6px] flex-1 rounded-full',
            index < lit ? 'bg-chalk' : 'bg-slate-700',
          )}
          style={{
            // Reduced motion keeps the waveform still and lets the lit count do
            // the talking; otherwise the whole shape breathes with the level.
            height: `${(reduced ? height : height * (0.22 + 0.78 * level)) * 100}%`,
            minHeight: '3px',
          }}
        />
      ))}
    </div>
  );
}

export function AudioStage({ recorder }: { recorder: RecorderApi }) {
  const reduced = usePrefersReducedMotion();
  const { level, measuring } = useInputLevel(recorder.micMonitor, reduced);
  const recording = recorder.phase === 'recording';
  const paused = recorder.phase === 'paused';
  const listening = recorder.hasMic && recorder.phase !== 'finishing';
  // Only accuse the microphone of silence when we are actually measuring it.
  const quiet = useSilenceWarning(level, listening && !paused && measuring);

  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      <div className="clapper-stripes absolute inset-x-0 top-0 z-10 h-1.5 opacity-[0.14]" aria-hidden="true" />

      <div className="relative flex min-h-[16rem] w-full flex-col items-center justify-center gap-4 px-6 py-10 sm:min-h-[19rem]">
        {recorder.hasMic ? (
          <>
            <Meter level={level} reduced={reduced} />
            <p className="text-caption text-chalk-faint" role="status">
              {paused
                ? 'Paused. Nothing is being recorded.'
                : quiet
                  ? 'Nothing is coming through. Check that the right microphone is selected and unmuted.'
                  : recording
                    ? 'Recording your voice.'
                    : 'Say something — the meter should move.'}
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-hairline bg-slate-850 text-chalk-faint">
              <MicOffIcon size={24} />
            </span>
            <p className="text-body text-chalk-dim">
              {recorder.phase === 'arming' ? 'Waiting for your microphone…' : 'No microphone yet'}
            </p>
            <p className="max-w-xs text-caption text-chalk-faint">
              {recorder.phase === 'arming'
                ? 'Your browser is asking whether Slate may listen. Choose allow.'
                : 'A voice note is nothing but the microphone, so Slate needs permission to use it before you can record.'}
            </p>
          </div>
        )}

        {recorder.phase === 'countdown' ? (
          <Countdown value={recorder.countdown} onSkip={recorder.skipCountdown} />
        ) : null}

        {recording || paused ? (
          <div className="absolute left-3 top-4 z-10 flex items-center gap-2 rounded bg-slate-950/80 px-2.5 py-1.5">
            <span
              className={cx(
                'h-2 w-2 rounded-full',
                recording ? 'animate-record-pulse bg-record' : 'bg-chalk-faint',
              )}
              aria-hidden="true"
            />
            <span className="slate-type text-caption text-chalk" data-timecode>
              {clock(recorder.elapsedMs / 1000)}
            </span>
            {paused ? <span className="text-micro uppercase text-chalk-faint">Paused</span> : null}
          </div>
        ) : null}

        {recorder.hasMic ? (
          <span className="absolute right-3 top-4 z-10 flex items-center gap-1.5 rounded bg-slate-950/70 px-2 py-1 text-micro uppercase tracking-[0.08em] text-chalk-faint">
            <MicIcon size={12} />
            Audio only
          </span>
        ) : null}
      </div>
    </div>
  );
}
